import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import Dropzone from "react-dropzone";
import axios from "axios";

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const UpdateOrganization = ({ isOpen, toggleModal, onSubmit,data }) => {
  console.log("🚀 ~ UpdateOrganization ~ data:", data)
  const [selectedBanner, setSelectedBanner] = useState([]); // Initialize state for selected banner image

  // Initial values for the form
  const initialValues = {
    name: data.name || "",
    bannerImage: data.bannerImage || "",
    organizationCode: data?.organizationCode || "",
    tagLine: data?.tagLine || "",
    address: data?.address || "",
    contactInfo: data?.contactInfo || "",
    website: data?.website || "",
  };

  // Validation Schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string().required("Organization Name is required"),
    bannerImage: Yup.string().required("Banner Image is required"),
    organizationCode: Yup.string().required("Organization Code is required"),
    tagLine: Yup.string().required("Tag Line is required"),
    address: Yup.string().required("Address is required"),
    contactInfo: Yup.string().required("Contact Info is required"),
    website: Yup.string()
      .url("Invalid URL format")
      .required("Website is required"),
  });

  // Handle the form submission
  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();

      // Append form data fields
      formData.append("name", values.name);
      formData.append("organizationCode", values.organizationCode);
      formData.append("tagLine", values.tagLine);
      formData.append("address", values.address);
      formData.append("contactInfo", values.contactInfo);
      formData.append("website", values.website);

      // If there's a file selected for banner, append it to the formData
      if (selectedBanner.length > 0) {
        formData.append("bannerImage", selectedBanner[0]); // Use the file object, not the name
      }
      console.log("Form data: ", formData);
      // Send the data to the server (use your actual API service)
      const response = await axios.put(
        `https://api-dev-qrmenu.essencetechnologies.com/api/organization/${data?.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Axios handles the boundary automatically
          },
        }
      );
      console.log("Response from server: ", response);
      onSubmit(response); // Handle the actual submit logic (e.g., show success message)
      toggleModal(); // Close modal after submission
    } catch (error) {
      console.error("Error during submission: ", error);
    }
  };

  // Handle file selection and updating Formik values
  const handleBannerFileSelect = (files, setFieldValue) => {
    const formattedFiles = files.map((file) => ({
      ...file,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    }));

    setSelectedBanner((prevFiles) => [...prevFiles, ...formattedFiles]);

    // Assuming single file upload for banner, assign the file's name to bannerImage
    if (files.length > 0) {
      setFieldValue("bannerImage", files[0].name); // Update Formik's field with the image name
    }
  };

  // Handle removing file from selectedBanner
  const handleFileRemove = (indexToRemove, setFieldValue) => {
    setSelectedBanner((prevFiles) => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      // If no files are left, reset the bannerImage in Formik
      if (newFiles.length === 0) {
        setFieldValue("bannerImage", ""); // Reset bannerImage when files are removed
      }
      return newFiles;
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg">
      <ModalHeader toggle={toggleModal}>Add Organization</ModalHeader>
      <ModalBody>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Dropzone
                      onDrop={(acceptedFiles) =>
                        handleBannerFileSelect(acceptedFiles, setFieldValue)
                      }
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div className="dropzone dz-clickable pt-3">
                          <div
                            className="dz-message needsclick mb-5 p-3"
                            {...getRootProps()}
                          >
                            <i className="align-item-center display-4 text-muted ri-upload-cloud-2-fill" />
                            <input {...getInputProps()} />
                            <h6 className="fs-15 fw-semi-bold pt-3">
                              Drop banner image here or{" "}
                              <Link to="#">click to upload.</Link>
                            </h6>
                            <div
                              className="list-unstyled mb-0"
                              id="file-previews"
                            >
                              {selectedBanner.map((f, i) => (
                                <Card
                                  className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                  key={i + "-file"}
                                >
                                  <div>
                                    <Row className="align-items-center">
                                      <Col className="col-auto">
                                        <img
                                          data-dz-thumbnail=""
                                          height="100"
                                          className="avatar-sm rounded bg-light"
                                          alt={f.name}
                                          src={f.preview}
                                        />
                                      </Col>
                                      <Col>
                                        <Link
                                          to="#"
                                          className="text-muted font-weight-bold"
                                        >
                                          {f.name}
                                        </Link>
                                        <p className="mb-0 fs-12">
                                          {f.formattedSize}
                                        </p>
                                      </Col>
                                      <Col lg={2}>
                                        <Link
                                          onClick={() =>
                                            handleFileRemove(i, setFieldValue)
                                          }
                                        >
                                          <h6>
                                            <i className="ri-delete-bin-2-line text-danger"></i>
                                          </h6>
                                        </Link>
                                      </Col>
                                    </Row>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </Dropzone>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="name">Organization Name</Label>
                        <Field
                          name="name"
                          type="text"
                          className="form-control"
                          id="name"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="organizationCode">Organization Code</Label>
                        <Field
                          name="organizationCode"
                          type="text"
                          className="form-control"
                          id="organizationCode"
                        />
                        <ErrorMessage
                          name="organizationCode"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="tagLine">Tag Line</Label>
                        <Field
                          name="tagLine"
                          type="text"
                          className="form-control"
                          id="tagLine"
                        />
                        <ErrorMessage
                          name="tagLine"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="address">Address</Label>
                        <Field
                          name="address"
                          type="text"
                          className="form-control"
                          id="address"
                        />
                        <ErrorMessage
                          name="address"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="contactInfo">Contact Info</Label>
                        <Field
                          name="contactInfo"
                          type="text"
                          className="form-control"
                          id="contactInfo"
                        />
                        <ErrorMessage
                          name="contactInfo"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="website">Website</Label>
                        <Field
                          name="website"
                          type="text"
                          className="form-control"
                          id="website"
                        />
                        <ErrorMessage
                          name="website"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit">
                  Submit
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default UpdateOrganization;
